import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const TextFieldInput = ({
  name,
  control,
  label,
  required = false,
  multiline = false,
  rows = 1,
  minRows,
  maxRows,
  disabled=false,
  sx = {},
  varchar,
  size,
  enableBlurHandler = false,
  onBlurErrorMsg = '',
  onCustomBlur,
  pattern,
  patternErrorMsg,
  transform,
  inputType = 'text',
  placeholder,
}) => {
  const { setError, clearErrors } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = inputType === 'password';

  const labelWithAsterisk = required ? `${label} *` : label;
  const getErrorState = (error, customErrorMsg) => {
    const hasCustomError = !!customErrorMsg;
    const hasValidationError = !!error;

    return {
      error: hasCustomError || hasValidationError,
      helperText: hasCustomError ? customErrorMsg : error?.message || '',
    };
  };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const { error: isError, helperText } = getErrorState(error, onBlurErrorMsg);
        return (
          <TextField
            {...field}
            fullWidth
            label={labelWithAsterisk}
            type={isPassword ? (showPassword ? 'text' : 'password') : 'text'}
            size={size}
            disabled={disabled}
            error={isError}
            helperText={helperText}
            multiline={multiline}
            /* Multiline fields use minRows/maxRows (react-textarea-autosize)
               instead of a fixed `rows`, so the textarea grows with the
               content. Only pass maxRows when the caller explicitly wants
               a capped height with an internal scrollbar -- otherwise leave
               it unset so the field keeps expanding and never scrolls. */
            rows={!multiline ? rows : undefined}
            minRows={multiline ? minRows ?? rows : undefined}
            maxRows={multiline ? maxRows : undefined}
            value={field.value ?? ''}
            sx={sx}
            placeholder={placeholder}
            {...(placeholder ? { InputLabelProps: { shrink: true } } : {})}
            onChange={(e) => {
              let inputValue = e.target.value;
              if (transform) {
                inputValue = transform(inputValue);
              }
              clearErrors(name);
              field.onChange(inputValue);
            }}
            onBlur={
              enableBlurHandler
                ?  (e, name) => {
                    field.onBlur();
                    onCustomBlur && onCustomBlur(e.target.value, name);
                    if (pattern) {
                      if (!pattern?.test(e.target.value)) {
                        setError(name, {
                          type: "manual",
                          message: patternErrorMsg || "Invalid format",
                        });
                      } else {
                        clearErrors(name);
                      }
                    }
                  }
                : undefined
            }
            inputProps={{
              ...(varchar > 0 ? { maxLength: varchar } : {}),
            }}
            InputProps={{
              ...(isPassword ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              } : {}),
            }}
          />
        );
      }}
    />
  );
};

export default TextFieldInput;